import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Alert, ToggleButtonGroup, ToggleButton, Button, alpha } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import SyncIcon from '@mui/icons-material/Sync';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DownloadIcon from '@mui/icons-material/Download';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources } from '../authConfig';
import DashboardCard from '../components/StatisticsDashboard/DashboardCard';
import MonthRangeFilter from '../components/StatisticsDashboard/MonthRangeFilter';
import StatTile from '../components/StatisticsDashboard/StatTile';
import TrendAreaChart from '../components/StatisticsDashboard/TrendAreaChart';
import CategoryProgressList from '../components/StatisticsDashboard/CategoryProgressList';
import ProcessHealthList from '../components/StatisticsDashboard/ProcessHealthList';
import { getDefaultMonthRange, monthInputToRange, CATEGORICAL_COLORS, STATUS_COLORS } from '../components/StatisticsDashboard/chartUtils';
import NotFoundPage from './ErrorPage';

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || '').trim();

// Entry to /AutomationStatistics only requires the "ManagersDashboardPage" role
// permission (checked by ProtectedRoute). Whether this specific sub-dashboard
// is enabled is a separate, narrower grant living in managerDashboardPermissionModel
// — so it's checked here, locally, against /api/managerDashboard/myAccess.
const AUTOMATION_DASHBOARD_KEY = 'automationStats';

const PROJECT_OPTIONS = [
    { key: 'poConfirmation', label: 'אישורי הזמנות רכש' },
    { key: 'invoiceUpdater', label: 'עדכון שדות בחשבונית' },
    { key: 'both', label: 'שני התהליכים' },
];

const SENSITIVE = '#c62828';

const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const AutomationStatisticsPage = () => {
    // "checking" | "granted" | "denied"
    const [dashboardAccess, setDashboardAccess] = useState('checking');
    const [canExportMapping, setCanExportMapping] = useState(false);

    const [selection, setSelection] = useState('both');
    const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
    const [toMonth, setToMonth] = useState(DEFAULT_TO);

    const [comparison, setComparison] = useState({});
    const [summary, setSummary] = useState(null);
    const [userStats, setUserStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exportState, setExportState] = useState({ loading: false, error: null });

    const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
    const { instance, accounts } = useMsal();

    useEffect(() => {
        let cancelled = false;

        // A definitive 403 means the backend evaluated access and said no.
        // Anything else (network hiccup, cold-start 5xx, token refresh blip) is a
        // failed check, not a denial — retry once before giving up.
        const checkAccess = async (isRetry = false) => {
            try {
                const result = await fetchRead('GET', `${BASE_URL}/api/managerDashboard/myAccess`);
                if (cancelled) return;
                const allowed = !!result?.isAdmin || !!result?.allowedDashboards?.includes(AUTOMATION_DASHBOARD_KEY);
                setDashboardAccess(allowed ? 'granted' : 'denied');
                setCanExportMapping(!!result?.canExportAutomationUserMapping);
            } catch (err) {
                if (cancelled) return;
                if (err?.status === 403 || isRetry) {
                    setDashboardAccess('denied');
                    return;
                }
                setTimeout(() => checkAccess(true), 500);
            }
        };

        checkAccess();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStats = useCallback(async () => {
        if (!fromMonth || !toMonth) return;
        setLoading(true);
        setError(null);
        try {
            const { startDate, endDate } = monthInputToRange(fromMonth, toMonth);
            const projects = selection === 'both' ? 'poConfirmation,invoiceUpdater' : selection;
            const params = new URLSearchParams({ startDate, endDate, projects }).toString();
            const [statsRes, byUserRes] = await Promise.all([
                fetchRead('GET', `${BASE_URL}/api/automation-stats?${params}`),
                fetchRead('GET', `${BASE_URL}/api/automation-stats/by-user?${params}`),
            ]);
            setComparison(statsRes.comparison || {});
            setSummary(statsRes.summary || null);
            setUserStats(byUserRes.userStats || []);
        } catch (err) {
            console.error('Error loading automation statistics:', err);
            setError(err.message || 'שגיאה בטעינת הסטטיסטיקות');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromMonth, toMonth, selection]);

    useEffect(() => {
        if (dashboardAccess === 'granted') loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardAccess, fromMonth, toMonth, selection]);

    // Downloads the real email <-> pseudonym CSV. Kept separate from
    // useFetchWithMsal (which always parses JSON) since this response is a
    // file blob; gated in the UI by canExportMapping, and independently
    // enforced server-side by checkAutomationUserMappingExportAccess.
    const handleExportMapping = async () => {
        setExportState({ loading: true, error: null });
        try {
            const account = accounts[0];
            const result = await instance.acquireTokenSilent({
                scopes: protectedResources.apiScopes.scopes.read,
                account,
            });
            // Scoped to whatever the user currently has selected (project toggle
            // + date range) so the file matches what's on screen.
            const { startDate, endDate } = monthInputToRange(fromMonth, toMonth);
            const projects = isCombined ? 'poConfirmation,invoiceUpdater' : selection;
            const params = new URLSearchParams({ startDate, endDate, projects }).toString();
            const response = await fetch(`${BASE_URL}/api/automation-stats/user-mapping/export?${params}`, {
                headers: { Authorization: `Bearer ${result.accessToken}` },
            });
            if (!response.ok) throw new Error(`שגיאה בהורדת קובץ המיפוי (${response.status})`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `automation-user-mapping-${projects.replace(',', '-')}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setExportState({ loading: false, error: null });
        } catch (err) {
            console.error('Error exporting automation user mapping:', err);
            setExportState({ loading: false, error: err.message || 'שגיאה בהורדת קובץ המיפוי' });
        }
    };

    const isCombined = selection === 'both';
    const activeStats = isCombined ? summary : comparison[selection];

    const comparisonRows = useMemo(() => {
        if (!isCombined) return [];
        return Object.values(comparison).map((project) => ({ name: project.label, count: project.totalRuns }));
    }, [isCombined, comparison]);

    const pageColor = CATEGORICAL_COLORS[0];

    if (dashboardAccess === 'checking') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress sx={{ color: pageColor }} />
            </Box>
        );
    }

    if (dashboardAccess === 'denied') {
        return <NotFoundPage />;
    }

    return (
        <Box display="flex" flexDirection="column" gap={3} p={2}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: alpha(pageColor, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <SyncIcon sx={{ color: pageColor, fontSize: 26 }} />
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight={700}>מעקב תהליכי אוטומציה</Typography>
                    <Typography variant="body2" color="text.secondary">
                        התקדמות תהליכי האוטומציה — אישורי הזמנות רכש ועדכון שדות בחשבונית
                    </Typography>
                </Box>
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
                <ToggleButtonGroup
                    value={selection}
                    exclusive
                    onChange={(_, value) => value && setSelection(value)}
                    size="small"
                    sx={{ bgcolor: '#fff', borderRadius: 3, p: 0.5, border: '1px solid rgba(11,11,11,0.08)' }}
                >
                    {PROJECT_OPTIONS.map((option) => (
                        <ToggleButton key={option.key} value={option.key} sx={{ borderRadius: 2.5, px: 2, textTransform: 'none' }}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {canExportMapping && (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        p: 2,
                        borderRadius: 4,
                        border: `1px solid ${alpha(SENSITIVE, 0.25)}`,
                        bgcolor: alpha(SENSITIVE, 0.05),
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: alpha(SENSITIVE, 0.14),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <DownloadIcon sx={{ color: SENSITIVE, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" fontWeight={700}>מיפוי כינוי-מייל</Typography>
                            <Typography variant="caption" color="text.secondary">
                                קובץ CSV עם כתובות המייל האמיתיות מאחורי הכינויים, לתהליכים שנבחרו למעלה
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={exportState.loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon />}
                        onClick={handleExportMapping}
                        disabled={exportState.loading}
                        sx={{
                            borderRadius: 2.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: SENSITIVE,
                            '&:hover': { bgcolor: alpha(SENSITIVE, 0.85) },
                        }}
                    >
                        הורדה
                    </Button>
                </Paper>
            )}

            {exportState.error && <Alert severity="error" sx={{ borderRadius: 3 }}>{exportState.error}</Alert>}

            <MonthRangeFilter
                fromMonth={fromMonth}
                toMonth={toMonth}
                onFromMonthChange={setFromMonth}
                onToMonthChange={setToMonth}
                color={pageColor}
            />

            {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}

            {loading || !activeStats ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} p={6}>
                    <CircularProgress sx={{ color: pageColor }} />
                    <Typography variant="body2" color="text.secondary">טוען נתונים...</Typography>
                </Box>
            ) : (
                <>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatTile label="סה״כ הרצות" value={activeStats.totalRuns.toLocaleString('he-IL')} icon={PlayCircleFilledIcon} color={CATEGORICAL_COLORS[0]} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatTile label="הצליחו" value={activeStats.completed.toLocaleString('he-IL')} icon={CheckCircleIcon} color={STATUS_COLORS.good} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatTile label="נכשלו" value={activeStats.failed.toLocaleString('he-IL')} icon={CancelIcon} color={STATUS_COLORS.critical} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatTile label="בתהליך" value={activeStats.inProgress.toLocaleString('he-IL')} icon={HourglassEmptyIcon} color={STATUS_COLORS.warning} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={4} alignItems="stretch">
                        <Grid item xs={12} md={isCombined ? 6 : 12}>
                            <DashboardCard
                                title="מגמה חודשית"
                                subtitle="סה״כ הרצות לאורך זמן"
                                icon={TrendingUpIcon}
                                color={CATEGORICAL_COLORS[0]}
                            >
                                {(expanded) => (
                                    <TrendAreaChart
                                        monthlyData={activeStats.monthlyStats}
                                        expanded={expanded}
                                        color={CATEGORICAL_COLORS[0]}
                                        totalLabel="סה״כ הרצות בטווח שנבחר"
                                        unitLabel="הרצות"
                                        gradientId="automationTrendGradient"
                                    />
                                )}
                            </DashboardCard>
                        </Grid>

                        {isCombined && (
                            <Grid item xs={12} md={6}>
                                <DashboardCard
                                    title="השוואה בין התהליכים"
                                    subtitle="סה״כ הרצות לכל תהליך בטווח שנבחר"
                                    icon={CompareArrowsIcon}
                                    color={CATEGORICAL_COLORS[1]}
                                >
                                    {() => <CategoryProgressList data={comparisonRows} unitLabel="הרצות" />}
                                </DashboardCard>
                            </Grid>
                        )}

                        <Grid item xs={12} md={isCombined ? 12 : 6}>
                            <DashboardCard
                                title="פילוח לפי משתמש"
                                subtitle="כמות הרצות לכל משתמש (מזוהה בכינוי בלבד)"
                                icon={PeopleAltIcon}
                                color={CATEGORICAL_COLORS[2]}
                            >
                                {() => <CategoryProgressList data={userStats.map((u) => ({ name: u.pseudonym, count: u.count }))} unitLabel="הרצות" />}
                            </DashboardCard>
                        </Grid>

                        <Grid item xs={12}>
                            <DashboardCard
                                title="בריאות תהליכים"
                                subtitle="ההרצה האחרונה של כל תהליך — סטטוס וזמן"
                                icon={MonitorHeartIcon}
                                color={CATEGORICAL_COLORS[3]}
                            >
                                {() => <ProcessHealthList projects={Object.values(comparison)} />}
                            </DashboardCard>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default AutomationStatisticsPage;
